import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { Scissors, MessageCircle, Trash2, Edit2, Check, Loader2, X, Star } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { StatusBadge } from '../../ui/StatusBadge';
import { dbService } from '../../../services/dbService';
import { compressImage } from '../../../lib/imageUtils';

export const BookingItem = ({ b, index, onCancelBooking, onChat, unreadCount }: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isRating, setIsRating] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [newLocation, setNewLocation] = useState(b.location || 'Stadion Utama');
  const [isSaving, setIsSaving] = useState(false);

  const handleUpdateLocation = async () => {
    if (!newLocation.trim()) return;
    setIsSaving(true);
    try {
      await dbService.updateBookingLocation(b.id, newLocation);
      setIsEditingLocation(false);
    } catch (error) {
      console.error('Failed to update location:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRate = async () => {
    setIsSaving(true);
    try {
      await dbService.rateBooking(b.id, b.barberId, rating, review);
      setIsRating(false);
    } catch (error) {
      console.error('Failed to rate booking:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      key={b.id} 
      className="group bg-white rounded-2xl border border-slate-100 hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      <div className="p-4 sm:p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm",
            b.status === 'Selesai' ? "bg-emerald-50 text-emerald-500" : "bg-slate-50 text-slate-400"
          )}>
            <Scissors className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-black text-slate-900 text-base tracking-tight truncate">{b.serviceName || 'Premium Cut'}</h3>
              <span className="text-[8px] font-black text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 uppercase">
                #{b.id.slice(-4).toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold uppercase tracking-tight">
              <span>{b.barberName}</span>
              <span>•</span>
              <span>{b.time}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between w-full md:w-auto md:justify-end gap-4 md:border-l border-slate-100 md:pl-6">
          <div className="text-left md:text-right">
            <p className="text-lg font-black text-slate-900 leading-none mb-1">Rp {b.totalPrice.toLocaleString()}</p>
            <StatusBadge status={b.status} />
          </div>
          
            <div className="flex items-center gap-2">
            <button 
              onClick={() => onChat(b.id)}
              className="relative w-10 h-10 bg-slate-50 text-slate-400 rounded-xl hover:bg-emerald-500 hover:text-white transition-all flex items-center justify-center"
            >
              <MessageCircle className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[7px] font-black rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                  {unreadCount}
                </span>
              )}
            </button>
            {b.status === 'Menunggu' && (
              <button 
                onClick={() => onCancelBooking(b.id)}
                className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl hover:bg-red-500 hover:text-white transition-all flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                "h-10 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                isExpanded ? "bg-slate-900 text-white shadow-lg" : "bg-white border border-slate-100 text-slate-400 hover:border-slate-300"
              )}
            >
              {isExpanded ? 'Tutup' : 'Detail'}
            </button>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-slate-50"
          >
            <div className="p-5 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Tanggal</p>
                <p className="text-xs font-bold text-slate-700">{format(new Date(b.date), 'd MMM yyyy', { locale: id })}</p>
              </div>
              <div>
                <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Lokasi</p>
                {isEditingLocation ? (
                  <div className="flex items-center gap-2">
                    <input 
                      type="text"
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      className="text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none focus:border-emerald-500 w-full"
                      autoFocus
                    />
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={handleUpdateLocation}
                        disabled={isSaving}
                        className="p-1 text-emerald-500 hover:bg-emerald-50 rounded transition-colors"
                      >
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      </button>
                      <button 
                        onClick={() => {
                          setIsEditingLocation(false);
                          setNewLocation(b.location || 'Stadion Utama');
                        }}
                        disabled={isSaving}
                        className="p-1 text-slate-400 hover:bg-slate-200 rounded transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 group/loc">
                  <p className="text-xs font-bold text-slate-700">{b.location || 'Stadion Utama'}</p>
                    {b.status === 'Menunggu' && (
                      <button 
                        onClick={() => setIsEditingLocation(true)}
                        className="p-1 text-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Catatan</p>
                <p className="text-xs text-slate-500 font-medium italic">Datang 10 menit lebih awal.</p>
              </div>
            </div>

            {/* Payment Info Section */}
            <div className="mx-5 mb-5 p-4 bg-white rounded-2xl border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-[8px] font-black text-slate-400 uppercase">Metode & Status Pembayaran</p>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider",
                    b.paymentStatus === 'Lunas' ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                    b.paymentStatus === 'Menunggu Verifikasi' ? "bg-amber-50 text-amber-600 border border-amber-100" :
                    "bg-rose-50 text-rose-600 border border-rose-100"
                  )}>
                    {b.paymentStatus || 'Belum Bayar'}
                  </span>
                  <span className="text-xs font-bold text-slate-700">
                    via {b.paymentMethod || 'QRIS'}
                  </span>
                </div>
              </div>

              {/* Action: View Receipt or Upload Receipt */}
              <div className="flex items-center gap-3">
                {b.paymentProofUrl ? (
                  <div className="flex items-center gap-2">
                    <div className="relative group/proof cursor-pointer" onClick={() => {
                      const w = window.open();
                      if (w) w.document.write(`<img src="${b.paymentProofUrl}" style="max-width: 100%; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />`);
                    }}>
                      <img src={b.paymentProofUrl} className="w-10 h-10 rounded-xl object-cover border border-slate-200 hover:scale-105 transition-transform" />
                      <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover/proof:opacity-100 transition-opacity flex items-center justify-center text-white text-[8px] font-bold">Buka</div>
                    </div>
                    <div>
                      <p className="text-[8px] font-black text-slate-400 uppercase">Bukti Transfer</p>
                      <button 
                        onClick={() => {
                          const w = window.open();
                          if (w) w.document.write(`<img src="${b.paymentProofUrl}" style="max-width: 100%; border-radius: 12px;" />`);
                        }}
                        className="text-[10px] font-bold text-emerald-600 hover:underline text-left"
                      >
                        Lihat Bukti
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Unggah Bukti Transfer</label>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const reader = new FileReader();
                          reader.onloadend = async () => {
                            const compressed = await compressImage(reader.result as string, 600, 600, 0.7);
                            await dbService.updateBookingPayment(b.id, b.paymentMethod || 'QRIS', compressed);
                          };
                          reader.readAsDataURL(file);
                        } catch (err) {
                          console.error('Error uploading payment proof inline:', err);
                        }
                      }}
                      className="text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-emerald-500 file:text-white file:hover:bg-emerald-600 cursor-pointer"
                    />
                  </div>
                )}
              </div>
            </div>

            {b.status === 'Selesai' && !b.rating && !isRating && (
              <div className="mt-4 pt-4 border-t border-slate-50">
                <button 
                  onClick={() => setIsRating(true)}
                  className="w-full py-2.5 bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
                >
                  <Star className="w-3.5 h-3.5 fill-white" />
                  Beri Rating & Ulasan
                </button>
              </div>
            )}

            {isRating && (
              <div className="mt-4 pt-4 border-t border-slate-50 space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <p className="text-[9px] font-black text-slate-900 uppercase">Beri Bintang</p>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button 
                        key={star}
                        onClick={() => setRating(star)}
                        className="transition-transform active:scale-125"
                      >
                        <Star 
                          className={cn(
                            "w-6 h-6 transition-colors",
                            star <= rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
                          )} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[9px] font-black text-slate-900 uppercase ml-1">Ulasan</p>
                  <textarea 
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                    placeholder="Ceritakan pengalaman Anda..."
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 text-xs font-bold focus:border-emerald-500 transition-all outline-none resize-none"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setIsRating(false)}
                    className="flex-1 py-2.5 bg-slate-100 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Batal
                  </button>
                  <button 
                    onClick={handleRate}
                    disabled={isSaving}
                    className="flex-[2] py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Kirim Ulasan'}
                  </button>
                </div>
              </div>
            )}

            {b.rating && (
              <div className="mt-4 pt-4 border-t border-slate-50 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-black text-slate-900 uppercase">Penilaian Anda</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={cn(
                          "w-3 h-3",
                          star <= b.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
                        )} 
                      />
                    ))}
                  </div>
                </div>
                {b.review && (
                  <p className="text-[11px] font-medium text-slate-500 bg-slate-50 p-3 rounded-xl italic">
                    "{b.review}"
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
